import * as React from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: string;
  error?: string;
  helpText?: string;
  showRequirements?: boolean;
}

export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  PasswordInputProps
>(
  (
    { label, error, helpText, showRequirements, className, id, ...props },
    ref
  ) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const { t } = useTranslation();
    const inputId = id || props.name;

    const toggleVisibility = () => setIsVisible(!isVisible);

    return (
      <div className="space-y-2">
        {label && (
          <Label
            htmlFor={inputId}
            className={cn(error && "text-destructive")}
          >
            {label}
          </Label>
        )}
        <div className="relative">
          <Input
            ref={ref}
            id={inputId}
            type={isVisible ? "text" : "password"}
            className={cn(
              "pr-10",
              error && "border-destructive focus-visible:ring-destructive",
              className
            )}
            aria-invalid={!!error}
            {...props}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggleVisibility}
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            tabIndex={-1}
            aria-label={isVisible ? t("auth.hidePassword") : t("auth.showPassword")}
          >
            {isVisible ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
        {error && (
          <p className="text-sm font-medium text-destructive">{error}</p>
        )}
        {showRequirements && !error && (
          <p className="text-xs text-muted-foreground">
            {t("auth.passwordRequirements")}
          </p>
        )}
        {helpText && !error && !showRequirements && (
          <p className="text-sm text-muted-foreground">{helpText}</p>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";
